import React, { useState, useMemo } from 'react';
import { Grid, Box } from '@mui/material';
import { useFlavors } from '../../hooks/useFlavors';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { getAllCategories, getAllProductTypes, Filters } from '../../utils/filterUtils';
import Layout from '../layout/Layout';
import FlavorGrid from '../flavor/FlavorGrid';
import FilterPanel from '../search/FilterPanel';
import SortSelector from '../search/SortSelector';
import TabPanel from '../user/TabPanel';
import EditionHeader from '../edition/EditionHeader';

// Extended Filters interface to include edition
interface ExtendedFilters extends Filters {
  edition?: string;
}

const HomePage: React.FC = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('all');
  
  // Use flavors hook
  const {
    flavors,
    filteredFlavors,
    filters,
    sortBy,
    loading,
    error,
    updateFilters,
    resetFilters,
    updateSort
  } = useFlavors();
  
  // Use user preferences hook
  const {
    addFavorite,
    removeFavorite,
    checkIsFavorite,
    addTriedFlavor,
    checkHasTried,
    getFlavorNote,
    updateFlavorNote,
    getFlavorRating,
    rateFlavor
  } = useUserPreferences();
  
  // Get all categories
  const categories = getAllCategories(flavors);
  
  // Get all product types
  const productTypes = useMemo(() => {
    return getAllProductTypes(flavors);
  }, [flavors]);
  
  // Get all editions, filtered by product type if one is selected
  const editions = useMemo(() => {
    const editionSet = new Set<string>();
    flavors.forEach(flavor => {
      // Only include editions for the selected product type, or all if no product type is selected
      if (flavor.edition && (filters.productType === 'All' || flavor.productType === filters.productType)) {
        editionSet.add(flavor.edition);
      }
    });
    return Array.from(editionSet).sort();
  }, [flavors, filters.productType]);
  
  // Handle search
  const handleSearch = (query: string) => {
    updateFilters({ query });
  };
  
  // Handle tab change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  // Filter flavors based on active tab and edition
  const displayedFlavors = useMemo(() => {
    return filteredFlavors.filter(flavor => {
      // Filter by tab
      if (activeTab === 'favorites' && !checkIsFavorite(flavor.id)) {
        return false;
      }
      if (activeTab === 'tried' && !checkHasTried(flavor.id)) {
        return false;
      }
      // Filter by product type tabs
      if (activeTab === 'pulse' && flavor.productType !== 'Pulse') {
        return false;
      }
      if (activeTab === 'pulseX' && flavor.productType !== 'Pulse X') {
        return false;
      }
      
      // Filter by edition
      const editionFilter = (filters as ExtendedFilters).edition;
      if (editionFilter && flavor.edition !== editionFilter) {
        return false;
      }
      
      return true;
    });
  }, [filteredFlavors, activeTab, checkIsFavorite, checkHasTried, filters]);
  
  return (
    <Layout>
      <TabPanel activeTab={activeTab} onTabChange={handleTabChange} onSearch={handleSearch} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FilterPanel
            categories={categories}
            editions={editions}
            productTypes={productTypes}
            filters={filters as ExtendedFilters}
            onUpdateFilters={updateFilters}
            onResetFilters={resetFilters}
          />
        </Grid>
        
        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <SortSelector sortBy={sortBy} onSortChange={updateSort} />
          </Box>
          
          {/* Display the edition header if an edition is selected */}
          <EditionHeader 
            edition={(filters as ExtendedFilters).edition || ''} 
            productType={filters.productType !== 'All' ? filters.productType : 'Pulse'}
          />
          
          <FlavorGrid
            flavors={displayedFlavors}
            loading={loading}
            error={error}
            checkIsFavorite={checkIsFavorite}
            checkHasTried={checkHasTried}
            getFlavorRating={getFlavorRating}
            getFlavorNote={getFlavorNote}
            onAddToFavorites={addFavorite}
            onRemoveFromFavorites={removeFavorite}
            onMarkAsTried={addTriedFlavor}
            onRateFlavor={rateFlavor}
            onSaveNote={updateFlavorNote}
          />
        </Grid>
      </Grid>
    </Layout>
  );
};

export default HomePage;
